import React, { useState } from 'react';
import {
  View,
  Text,
  TouchableOpacity,
  StyleSheet,
  SafeAreaView,
  Platform,
} from 'react-native';
import SignOutConfirmModal from './SignOutConfirmModal';

interface BusinessOwnerBottomMenuProps {
  activeTab: 'dashboard' | 'appointments' | 'analytics' | 'settings' | 'logout';
  onTabPress: (tab: 'dashboard' | 'appointments' | 'analytics') => void;
  onNavigateToSettings?: () => void;
  onLogout?: () => void;
}

const BusinessOwnerBottomMenu: React.FC<BusinessOwnerBottomMenuProps> = ({
  activeTab,
  onTabPress,
  onNavigateToSettings,
  onLogout,
}) => {
  const [showSignOutModal, setShowSignOutModal] = useState(false);

  const handleButtonPress = (buttonId: string) => {
    switch (buttonId) {
      case 'settings':
        onNavigateToSettings?.();
        break;
      case 'logout':
        setShowSignOutModal(true);
        break;
      default:
        onTabPress(buttonId as any);
    }
  };

  const menuItems = [
    {
      id: 'dashboard' as const,
      label: 'Home',
      icon: '⌂',
      description: 'Overview & Stats',
    },
    {
      id: 'appointments' as const,
      label: 'Bookings',
      icon: '✓',
      description: 'Manage Bookings',
    },
    {
      id: 'analytics' as const,
      label: 'Analytics',
      icon: '≡',
      description: 'Reports & Insights',
    },
    {
      id: 'settings' as const,
      label: 'Settings',
      icon: '⋮',
      description: 'Account & Settings',
    },
    {
      id: 'logout' as const,
      label: 'Sign out',
      icon: '→',
      description: 'Sign Out',
    },
  ];

  return (
    <>
      <SignOutConfirmModal
        visible={showSignOutModal}
        onCancel={() => setShowSignOutModal(false)}
        onConfirm={() => {
          setShowSignOutModal(false);
          onLogout?.();
        }}
      />
      <SafeAreaView 
        style={styles.container}
        {...(Platform.OS === 'web' ? {
          // @ts-ignore - Web-specific style override
          style: [styles.container, { position: 'fixed' }]
        } : {})}
      >
        <View style={styles.menuBar}>
        {menuItems.map((item) => (
          <TouchableOpacity
            key={item.id}
            style={[
              styles.menuItem,
              activeTab === item.id && styles.activeMenuItem,
            ]}
            onPress={() => handleButtonPress(item.id)}
            activeOpacity={0.7}
          >
            <Text style={[
              styles.menuIcon,
              activeTab === item.id && styles.activeMenuIcon,
            ]}>
              {item.icon}
            </Text>
            <Text style={[
              styles.menuLabel,
              activeTab === item.id && styles.activeMenuLabel,
            ]}>
              {item.label}
            </Text>
          </TouchableOpacity>
        ))}
      </View>
    </SafeAreaView>
    </>
  );
};

const styles = StyleSheet.create({
  container: {
    backgroundColor: '#FFFFFF',
    borderTopWidth: 1,
    borderTopColor: '#E5E5E7',
    ...(Platform.OS === 'web' ? {
      position: 'fixed' as any, // Fixed position for web
    } : {
      position: 'absolute', // Absolute for mobile
    }),
    bottom: 0,
    left: 0,
    right: 0,
    zIndex: 1000,
    width: Platform.OS === 'web' ? '100%' : undefined, // Ensure full width on web
  },
  menuBar: {
    flexDirection: 'row',
    backgroundColor: '#FFFFFF',
    paddingHorizontal: 8,
    paddingVertical: 8,
    shadowColor: '#000',
    shadowOffset: {
      width: 0,
      height: -2,
    },
    shadowOpacity: 0.1,
    shadowRadius: 4,
    elevation: 5,
  },
  menuItem: {
    flex: 1,
    alignItems: 'center',
    justifyContent: 'center',
    paddingVertical: 10,
    paddingHorizontal: 4,
    borderRadius: 8,
    marginHorizontal: 2,
    backgroundColor: 'transparent',
  },
  activeMenuItem: {
    backgroundColor: 'rgba(0, 123, 255, 0.15)',
    borderWidth: 1,
    borderColor: 'rgba(0, 123, 255, 0.3)',
  },
  menuIcon: {
    fontSize: 22,
    marginBottom: 4,
    color: '#6C757D',
    ...(Platform.OS === 'web' ? {
      WebkitFontSmoothing: 'antialiased' as any,
      MozOsxFontSmoothing: 'grayscale' as any,
    } : {}),
  },
  activeMenuIcon: {
    color: '#007BFF',
  },
  menuLabel: {
    fontSize: 11,
    fontWeight: '500',
    color: '#6C757D',
    marginTop: 2,
    ...(Platform.OS === 'web' ? {
      WebkitFontSmoothing: 'antialiased' as any,
      MozOsxFontSmoothing: 'grayscale' as any,
      fontFamily: '-apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, "Helvetica Neue", Arial, sans-serif',
    } : {}),
  },
  activeMenuLabel: {
    color: '#007BFF',
    fontWeight: '600',
  },
});

export default BusinessOwnerBottomMenu;
